import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, MapPin, User, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = { requested: 'bg-amber-500/10 text-amber-700 dark:text-amber-400', scheduled: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', in_progress: 'bg-violet-500/10 text-violet-700 dark:text-violet-400', completed: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400', cancelled: 'bg-muted text-muted-foreground' };
const PRIO_STYLE = { low: 'bg-muted text-muted-foreground', medium: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', high: 'bg-amber-500/10 text-amber-700 dark:text-amber-400', critical: 'bg-red-500/10 text-red-700 dark:text-red-400' };
const ONSITE_STATUSES = ['requested', 'scheduled', 'in_progress', 'completed', 'cancelled'];

export default function OnsitePage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [assignDlg, setAssignDlg] = useState(null);
  const [editDlg, setEditDlg] = useState(null);
  const [assignEngineer, setAssignEngineer] = useState('');
  const [schedDate, setSchedDate] = useState('');
  // Master data
  const [requestTypes, setRequestTypes] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [products, setProducts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [users, setUsers] = useState([]);
  // Form
  const emptyForm = { ticket_id: '', request_type: '', city: '', retailer: '', description: '', customer_id: '', priority: 'medium', product_id: '', raised_by_customer: '', raised_by_retailer: '' };
  const [form, setForm] = useState({ ...emptyForm });
  const [editForm, setEditForm] = useState({});

  const load = useCallback(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.request_type = typeFilter;
    if (search) params.city = search;
    api.get('/onsite', { params }).then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); });
  }, [statusFilter, typeFilter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/masters/request_type').then(r => setRequestTypes(r.data.items || []));
    api.get('/retailers').then(r => setRetailers(r.data.retailers || []));
    api.get('/products').then(r => setProducts(r.data.products || []));
    api.get('/tickets').then(r => setTickets(r.data.tickets || []));
    api.get('/customers').then(r => setCustomers(r.data.customers || []));
    api.get('/masters/cities').then(r => setCities(r.data.cities || []));
    api.get('/users').then(r => setUsers(r.data.users || []));
  }, []);

  const create = async () => {
    try {
      await api.post('/onsite', form);
      toast.success('Onsite request created'); setDlgOpen(false); setForm({ ...emptyForm }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (id, status) => { await api.put(`/onsite/${id}`, { status }); toast.success('Updated'); load(); };

  const assignAndSchedule = async () => {
    const updates = {};
    if (assignEngineer) updates.assigned_engineer = assignEngineer;
    if (schedDate) updates.scheduled_date = schedDate;
    if (updates.assigned_engineer || updates.scheduled_date) {
      updates.status = 'scheduled';
    }
    await api.put(`/onsite/${assignDlg}`, updates);
    toast.success('Updated'); setAssignDlg(null); setAssignEngineer(''); setSchedDate(''); load();
  };

  const openEdit = (item) => {
    setEditDlg(item.onsite_id);
    setEditForm({ city: item.city || item.region || '', retailer: item.retailer || '', product_id: item.product_id || '', completion_notes: item.completion_notes || '' });
  };

  const saveEdit = async () => {
    await api.put(`/onsite/${editDlg}`, editForm);
    toast.success('Updated'); setEditDlg(null); load();
  };

  const del = async (id) => { await api.delete(`/onsite/${id}`); toast.success('Deleted'); load(); };
  const getProductName = (id) => products.find(p => p.product_id === id)?.name || id || '-';
  const getUserName = (id) => users.find(u => u.user_id === id)?.name || id || '-';

  return (
    <div data-testid="onsite-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold tracking-tight">Onsite Maintenance</h1><p className="text-sm text-muted-foreground mt-1">{total} requests</p></div>
        <Button onClick={() => setDlgOpen(true)} data-testid="create-onsite-btn"><Plus size={16} className="mr-1" /> New Request</Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} /><Input placeholder="Search city..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 w-48" /></div>
        <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}><SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{ONSITE_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
        <Select value={typeFilter || 'all'} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}><SelectTrigger className="w-40"><SelectValue placeholder="Request Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem>{requestTypes.map(t => <SelectItem key={t.master_id} value={t.name}>{t.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Request Type</TableHead><TableHead>City</TableHead><TableHead>Retailer</TableHead><TableHead>Product</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Engineer</TableHead><TableHead>Raised By</TableHead><TableHead>Date</TableHead><TableHead className="w-[50px]" />
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No onsite requests</TableCell></TableRow>}
            {items.map(item => (
              <TableRow key={item.onsite_id} className="hover:bg-muted/50" data-testid={`onsite-row-${item.onsite_id}`}>
                <TableCell className="font-medium text-sm">{item.request_type}</TableCell>
                <TableCell className="text-sm"><MapPin size={12} className="inline mr-1 text-muted-foreground" />{item.city || item.region || '-'}</TableCell>
                <TableCell className="text-xs">{item.retailer || '-'}</TableCell>
                <TableCell className="text-xs">{getProductName(item.product_id)}</TableCell>
                <TableCell><Badge variant="secondary" className={`text-xs ${PRIO_STYLE[item.priority]}`}>{item.priority}</Badge></TableCell>
                <TableCell><Badge variant="secondary" className={`text-xs ${STATUS_STYLE[item.status]}`}>{item.status?.replace('_', ' ')}</Badge></TableCell>
                <TableCell className="text-xs">{item.assigned_engineer ? <span><User size={12} className="inline mr-1" />{item.assigned_engineer}</span> : '-'}</TableCell>
                <TableCell className="text-xs">{item.raised_by_customer || item.raised_by_retailer || '-'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{item.created_at?.slice(0, 10)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={14} /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(item)}><Pencil size={12} className="mr-1.5" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setAssignDlg(item.onsite_id); setAssignEngineer(item.assigned_engineer || ''); setSchedDate(item.scheduled_date || ''); }}>Assign / Schedule</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {ONSITE_STATUSES.filter(s => s !== item.status).map(s => <DropdownMenuItem key={s} onClick={() => updateStatus(item.onsite_id, s)}>Set {s.replace('_', ' ')}</DropdownMenuItem>)}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => del(item.onsite_id)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* ─── Create Dialog ─── */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-onsite-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Onsite Request</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Request Type *</Label>
                <Select value={form.request_type || 'none'} onValueChange={v => setForm(f => ({ ...f, request_type: v === 'none' ? '' : v }))}><SelectTrigger data-testid="onsite-request-type"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">Select...</SelectItem>{requestTypes.map(t => <SelectItem key={t.master_id} value={t.name}>{t.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['low','medium','high','critical'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>City</Label>
                <Select value={form.city || 'none'} onValueChange={v => setForm(f => ({ ...f, city: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select city..." /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{cities.map(c => <SelectItem key={c.city} value={c.city}>{c.city} ({c.state})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Retailer</Label>
                <Select value={form.retailer || 'none'} onValueChange={v => setForm(f => ({ ...f, retailer: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{retailers.map(r => <SelectItem key={r.retailer_id} value={r.name}>{r.name}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Product</Label>
                <Select value={form.product_id || 'none'} onValueChange={v => setForm(f => ({ ...f, product_id: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Linked Ticket</Label>
                <Select value={form.ticket_id || 'none'} onValueChange={v => setForm(f => ({ ...f, ticket_id: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{tickets.map(t => <SelectItem key={t.ticket_id} value={t.ticket_id}>{t.title?.slice(0, 40)}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Raised By: Customer</Label><Input value={form.raised_by_customer} onChange={e => setForm(f => ({ ...f, raised_by_customer: e.target.value }))} placeholder="Customer name" /></div>
              <div className="space-y-1.5"><Label>Raised By: Retailer</Label><Input value={form.raised_by_retailer} onChange={e => setForm(f => ({ ...f, raised_by_retailer: e.target.value }))} placeholder="Retailer name" /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button><Button onClick={create} disabled={!form.request_type} data-testid="onsite-create-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Assign / Schedule Dialog ─── */}
      <Dialog open={!!assignDlg} onOpenChange={() => setAssignDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Assign / Schedule</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>Engineer</Label><Input value={assignEngineer} onChange={e => setAssignEngineer(e.target.value)} placeholder="Engineer name" data-testid="assign-engineer-input" /></div>
            <div className="space-y-1.5"><Label>Scheduled Date</Label><Input type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAssignDlg(null)}>Cancel</Button><Button onClick={assignAndSchedule} data-testid="assign-submit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Edit Dialog ─── */}
      <Dialog open={!!editDlg} onOpenChange={() => setEditDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-onsite-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Onsite Request</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>City</Label>
              <Select value={editForm.city || 'none'} onValueChange={v => setEditForm(f => ({ ...f, city: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Select city..." /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{cities.map(c => <SelectItem key={c.city} value={c.city}>{c.city}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Retailer</Label>
              <Select value={editForm.retailer || 'none'} onValueChange={v => setEditForm(f => ({ ...f, retailer: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{retailers.map(r => <SelectItem key={r.retailer_id} value={r.name}>{r.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Product</Label>
              <Select value={editForm.product_id || 'none'} onValueChange={v => setEditForm(f => ({ ...f, product_id: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Completion Notes</Label><Textarea value={editForm.completion_notes || ''} onChange={e => setEditForm(f => ({ ...f, completion_notes: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditDlg(null)}>Cancel</Button><Button onClick={saveEdit}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
